import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { OutlineNodeDto } from '../../api/queries/outlines';

const STATUS_COLORS: Record<string, string> = {
  not_started: '#9e9e9e',
  in_progress: '#2196f3',
  drafted: '#ff9800',
  revised: '#4caf50',
  complete: '#8bc34a',
};

interface OutlineTreeNodeProps {
  node: OutlineNodeDto;
  depth: number;
  isExpanded: boolean;
  isSelected: boolean;
  hasChildren: boolean;
  onToggleExpand: () => void;
  onSelect: () => void;
  onRename: (name: string) => void;
  children?: React.ReactNode;
}

export function OutlineTreeNode({
  node,
  depth,
  isExpanded,
  isSelected,
  hasChildren,
  onToggleExpand,
  onSelect,
  onRename,
  children,
}: OutlineTreeNodeProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(node.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    setDraft(node.name);
  }, [node.name]);

  const save = () => {
    setEditing(false);
    if (draft.trim() && draft.trim() !== node.name) {
      onRename(draft.trim());
    } else {
      setDraft(node.name);
    }
  };

  const statusColor = node.status ? STATUS_COLORS[node.status] : undefined;

  return (
    <Box>
      <ListItemButton
        selected={isSelected}
        onClick={onSelect}
        onDoubleClick={() => setEditing(true)}
        sx={{ pl: 1 + depth * 3, py: 0.5 }}
        dense
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            sx={{ mr: 0.5 }}
          >
            {isExpanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <Box sx={{ width: 34 }} />
        )}

        {statusColor && (
          <Tooltip title={node.status ?? ''}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: statusColor,
                mr: 1,
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}

        {editing ? (
          <TextField
            inputRef={inputRef}
            size="small"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') {
                setDraft(node.name);
                setEditing(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{ flex: 1 }}
          />
        ) : (
          <ListItemText
            primary={
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" noWrap>
                  {node.name}
                </Typography>
                {node.nodeType && (
                  <Chip
                    label={node.nodeType}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}
                {node.linkedChapterId && (
                  <Typography variant="caption" color="success.main">
                    linked
                  </Typography>
                )}
              </Stack>
            }
            secondary={
              node.synopsis ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  component="span"
                  sx={{ maxWidth: 400, display: 'block' }}
                >
                  {node.synopsis}
                </Typography>
              ) : undefined
            }
          />
        )}
      </ListItemButton>

      {hasChildren && (
        <Collapse in={isExpanded} unmountOnExit>
          {children}
        </Collapse>
      )}
    </Box>
  );
}
